/**
 * Coin Detail Page
 * Full in-app detail view for a single coin (replaces external CoinGecko link)
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import './VaultMarketPage.css';
import './CoinDetailPage.css';

const COINGECKO_URL = 'https://api.coingecko.com/api/v3';

/* Renders an AdSense slot from admin settings */
function AdSlot( { html } ) {
    if ( !html ) return null;
    return React.createElement( 'div', {
        className: 'cv-ad-slot',
        style: { margin: '16px 0', textAlign: 'center', minHeight: '60px' },
        dangerouslySetInnerHTML: { __html: html }
    } );
}
const PROXY_URL = ( window.chainvaultSettings && window.chainvaultSettings.siteUrl ) ? window.chainvaultSettings.siteUrl.replace(/\/+$/, '') + '/market' : COINGECKO_URL;
function getSettings() { return window.chainvaultSettings || {}; }
function getAdSlot3() { return (window.chainvaultSettings && window.chainvaultSettings.adSlot3) || ''; }
function getAdSlot4() { return (window.chainvaultSettings && window.chainvaultSettings.adSlot4) || ''; }
function getCoinGeckoHeaders() {
    const key = getSettings().coingeckoApiKey;
    return key ? { 'x-cg-demo-api-key': key } : {};
}

function formatPrice( p ) {
    if ( p == null ) return '-';
    if ( p >= 1000 ) return '$' + p.toLocaleString( 'en-US', { maximumFractionDigits: 2 } );
    if ( p >= 1    ) return '$' + p.toFixed( 2 );
    if ( p >= 0.01 ) return '$' + p.toFixed( 4 );
    return '$' + p.toFixed( 8 );
}
function formatLarge( num ) {
    if ( !num ) return '-';
    if ( num >= 1e12 ) return '$' + ( num / 1e12 ).toFixed( 2 ) + 'T';
    if ( num >= 1e9  ) return '$' + ( num / 1e9  ).toFixed( 2 ) + 'B';
    if ( num >= 1e6  ) return '$' + ( num / 1e6  ).toFixed( 2 ) + 'M';
    return '$' + num.toLocaleString();
}

const TIMEFRAMES = [
    { id: '1',   label: '24h' },
    { id: '7',   label: '7d'  },
    { id: '14',  label: '14d' },
    { id: '30',  label: '30d' },
    { id: '90',  label: '90d' },
    { id: '365', label: '1Y'  },
    { id: 'max', label: 'Max' },
];

function PriceChart( { data, positive } ) {
    if ( !data || data.length < 2 ) {
        return React.createElement( 'div', { className: 'cd-chart-empty' }, 'Loading chart...' );
    }
    const prices = data.map( p => p[ 1 ] );
    const min = Math.min( ...prices );
    const max = Math.max( ...prices );
    const range = max - min || 1;
    const w = 800;
    const h = 320;
    const points = prices.map( ( v, i ) => {
        const x = ( i / ( prices.length - 1 ) ) * w;
        const y = h - ( ( v - min ) / range ) * ( h - 20 ) - 10;
        return x + ',' + y;
    } ).join( ' ' );
    const color = positive ? '#16a34a' : '#dc2626';
    const fillPoints = `0,${h} ${points} ${w},${h}`;
    return React.createElement( 'svg', {
        className: 'cd-chart',
        viewBox: `0 0 ${w} ${h}`,
        preserveAspectRatio: 'none',
    },
        React.createElement( 'defs', null,
            React.createElement( 'linearGradient', { id: 'cd-grad', x1: '0', y1: '0', x2: '0', y2: '1' },
                React.createElement( 'stop', { offset: '0%', stopColor: color, stopOpacity: '0.25' } ),
                React.createElement( 'stop', { offset: '100%', stopColor: color, stopOpacity: '0' } )
            )
        ),
        React.createElement( 'polygon', { points: fillPoints, fill: 'url(#cd-grad)' } ),
        React.createElement( 'polyline', { points, fill: 'none', stroke: color, strokeWidth: '2' } )
    );
}

function CoinDetailPage() {
    const { coinId } = useParams();
    const navigate = useNavigate();
    const [ coin, setCoin ] = useState( null );
    const [ loading, setLoading ] = useState( true );
    const [ error, setError ] = useState( null );
    const [ chartData, setChartData ] = useState( [] );
    const [ timeframe, setTimeframe ] = useState( '7' );
    const [ news, setNews ] = useState( [] );
    const [ markets, setMarkets ] = useState( [] );
    const [ similarCoins, setSimilarCoins ] = useState( [] );
    const [ activeTab, setActiveTab ] = useState("chart");

        const fetchCoin = useCallback( async () => {
            const cacheKey = "cv_coin_data_" + coinId;
            try {
                setLoading( true );
                setError( null );
                // Cache first (12 min)
                try {
                    const cached = sessionStorage.getItem(cacheKey);
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        if (Date.now() - parsed.ts < 12 * 60 * 1000) {
                            setCoin(parsed.data);
                            setLoading(false);
                            return;
                        }
                    }
                } catch (e) {}
                // Retry up to 3 times on rate limit
                let lastErr = null;
                for (let attempt = 0; attempt < 3; attempt++) {
                    try {
                        const url = COINGECKO_URL + "/coins/" + coinId + "?localization=false&tickers=false&community_data=false&developer_data=false&sparkline=true";
                        const res = await fetch(url, { headers: getCoinGeckoHeaders() });
                        if (res.status === 429) {
                            await new Promise(r => setTimeout(r, 1800 * (attempt + 1)));
                            lastErr = new Error("Rate limited");
                            continue;
                        }
                        if (!res.ok) throw new Error("Coin not found");
                        const json = await res.json();
                        setCoin(json);
                        try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: json, ts: Date.now() })); } catch (e) {}
                        setLoading(false);
                        return;
                    } catch (e) {
                        lastErr = e;
                        if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
                    }
                }
                throw lastErr || new Error("Could not load");
            } catch (err) {
                const msg = (err && err.message) || "";
                if (msg.indexOf("Rate") >= 0) {
                    setError("CoinGecko rate limit hit. Please wait and refresh.");
                    toast.error("Rate limit hit, please wait");
                } else {
                    setError(msg);
                    toast.error("Could not load coin details");
                }
            } finally {
                setLoading( false );
            }
        }, [ coinId ] );

    const fetchChart = useCallback( async () => {
        try {
            const res = await fetch( `${ COINGECKO_URL }/coins/${ coinId }/market_chart?vs_currency=usd&days=${ timeframe }`, {
                headers: getCoinGeckoHeaders(),
            } );
            if ( !res.ok ) return;
            const json = await res.json();
            setChartData( json.prices || [] );
        } catch ( e ) { /* silent */ }
    }, [ coinId, timeframe ] );

    const fetchNews = useCallback( async () => {
        try {
            const url = 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/' + encodeURIComponent( coinId ) + '&count=6';
            const res = await fetch( url );
            if ( !res.ok ) return;
            const json = await res.json();
            setNews( json.items || [] );
        } catch ( e ) { /* silent — news is optional */ }
    }, [ coinId ] );

    const fetchMarkets = useCallback( async () => {
      const cacheKey = "cv_coin_markets_" + coinId;
      try { const c = sessionStorage.getItem(cacheKey); if (c) { const pp = JSON.parse(c); if (Date.now() - pp.ts < 300000) { setMarkets(pp.data); return; } } } catch (e) {}
      try {
        const res = await fetch( PROXY_URL + "/coins/" + coinId + "/tickers?include_exchange_logo=true&page=1&depth=true", { headers: getCoinGeckoHeaders() } );
        if (!res.ok) return;
        const json = await res.json();
        const sliced = ( json.tickers || [] ).slice(0, 15); setMarkets(sliced); try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: sliced, ts: Date.now() })); } catch (e) {}
      } catch (e) {}
    }, [ coinId ] );
    
    const fetchSimilar = useCallback( async () => {
      if ( !coin || !coin.categories || coin.categories.length === 0 ) return;
      const cacheKey = "cv_coin_similar_" + coinId;
      try { const c = sessionStorage.getItem(cacheKey); if (c) { const pp = JSON.parse(c); if (Date.now() - pp.ts < 300000) { setSimilarCoins(pp.data); return; } } } catch (e) {}
      try {
        const res = await fetch( COINGECKO_URL + "/coins/markets?vs_currency=usd&category=" + encodeURIComponent(coin.categories[0]) + "&per_page=6&page=1&sparkline=false&price_change_percentage=24h", { headers: getCoinGeckoHeaders() } );
        if (!res.ok) return;
        const json = await res.json();
        const filtered = ( json || [] ).filter(c => c.id !== coinId).slice(0,5); setSimilarCoins(filtered); try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: filtered, ts: Date.now() })); } catch (e) {}
      } catch (e) {}
    }, [ coin, coinId ] );
    
    const fetchCoinNews = useCallback( async () => {
      if (!coin) return;
      try {
        const name = (coin.name || "").toLowerCase();
        const symbol = (coin.symbol || "").toLowerCase();
        // Cache key per coin
        const cacheKey = "cv_coin_news_" + coinId;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.ts < 5 * 60 * 1000) { setNews(parsed.items); return; }
        }
        // Try the tag-specific feed first (works for major coins)
        let items = [];
        try {
          const tagRes = await fetch("https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/" + encodeURIComponent(symbol) + "&count=6");
          if (tagRes.ok) {
            const tagJson = await tagRes.json();
            if (tagJson.items && tagJson.items.length > 0) items = tagJson.items;
          }
        } catch (e) {}
        // Fallback: fetch main feed, filter by coin name/symbol
        if (items.length === 0) {
          const mainRes = await fetch("https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss&count=30");
          if (mainRes.ok) {
            const mainJson = await mainRes.json();
            const all = mainJson.items || [];
            items = all.filter(item => {
              const text = ((item.title || "") + " " + (item.description || "")).toLowerCase();
              return text.indexOf(name) >= 0 || (symbol.length >= 3 && text.indexOf(symbol) >= 0);
            }).slice(0, 6);
            // Last fallback: if still nothing, just show recent news (better than empty)
            if (items.length === 0) items = all.slice(0, 4);
          }
        }
        setNews(items);
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ items, ts: Date.now() })); } catch (e) {}
      } catch (e) { console.warn("[coin news] failed", e); }
    }, [ coin, coinId ] );
    
    useEffect( () => { fetchCoin(); }, [ fetchCoin ] );
    useEffect( () => { fetchMarkets(); }, [ fetchMarkets ] );
    useEffect( () => { fetchSimilar(); }, [ fetchSimilar ] );
    // useEffect( () => { fetchCoinNews(); }, [ fetchCoinNews ] ); // removed
    useEffect( () => { fetchChart(); }, [ fetchChart ] );
    useEffect( () => { fetchNews(); }, [ fetchNews ] );

    if ( loading && !coin ) {
        return React.createElement( 'div', { className: 'cv-page cd-page vm-wrap', 'data-theme': 'dark' },
            React.createElement( 'div', { className: 'cd-loading' }, 'Loading coin details...' )
        );
    }
    if ( error || !coin ) {
        return React.createElement( 'div', { className: 'cv-page cd-page vm-wrap', 'data-theme': 'dark' },
            React.createElement( 'div', { className: 'cd-error' },
                React.createElement( 'p', null, error || 'Coin not found' ),
                React.createElement( Link, { to: '/market', className: 'cd-back' }, '← Back to Market' )
            )
        );
    }

    const change24h = coin.market_data?.price_change_percentage_24h || 0;
    const positive  = change24h >= 0;
    const price     = coin.market_data?.current_price?.usd;
    const rank      = coin.market_cap_rank;
    const symbol    = ( coin.symbol || '' ).toUpperCase();

    const stats = [
        { label: 'Market Cap',         value: formatLarge( coin.market_data?.market_cap?.usd ) },
        { label: 'Fully Diluted Val.', value: formatLarge( coin.market_data?.fully_diluted_valuation?.usd ) },
        { label: '24h Volume',         value: formatLarge( coin.market_data?.total_volume?.usd ) },
        { label: 'Circulating Supply', value: coin.market_data?.circulating_supply ? coin.market_data.circulating_supply.toLocaleString() + ' ' + symbol : '-' },
        { label: 'Max Supply',         value: coin.market_data?.max_supply ? coin.market_data.max_supply.toLocaleString() + ' ' + symbol : '\u221E' },
        { label: 'Total Supply',       value: coin.market_data?.total_supply ? coin.market_data.total_supply.toLocaleString() + ' ' + symbol : '-' },
        { label: 'All-Time High',      value: formatPrice( coin.market_data?.ath?.usd ) },
        { label: 'All-Time Low',       value: formatPrice( coin.market_data?.atl?.usd ) },
    ];

    const changes = [
        { label: '1h',  v: coin.market_data?.price_change_percentage_1h_in_currency?.usd },
        { label: '24h', v: coin.market_data?.price_change_percentage_24h_in_currency?.usd },
        { label: '7d',  v: coin.market_data?.price_change_percentage_7d_in_currency?.usd },
        { label: '14d', v: coin.market_data?.price_change_percentage_14d_in_currency?.usd },
        { label: '30d', v: coin.market_data?.price_change_percentage_30d_in_currency?.usd },
        { label: '1y',  v: coin.market_data?.price_change_percentage_1y_in_currency?.usd },
    ];

    return React.createElement( 'div', { className: 'cv-page cd-page vm-wrap', 'data-theme': 'dark' },
        /* Back link */
        React.createElement( Link, { to: '/market', className: 'cd-back-link' }, '\u2190 Back to Market' ),
        React.createElement( AdSlot, { html: getAdSlot3() } ),

        /* Header */
        React.createElement( 'div', { className: 'cd-header' },
            React.createElement( 'div', { className: 'cd-header-left' },
                React.createElement( 'img', { src: coin.image?.large, alt: coin.name, className: 'cd-coin-img', onError: e => { e.target.style.display = 'none'; } } ),
                React.createElement( 'div', null,
                    React.createElement( 'div', { className: 'cd-coin-row' },
                        React.createElement( 'h1', { className: 'cd-coin-name' }, coin.name ),
                        React.createElement( 'span', { className: 'cd-coin-symbol' }, symbol ),
                        rank && React.createElement( 'span', { className: 'cd-coin-rank' }, '#' + rank )
                    ),
                    React.createElement( 'div', { className: 'cd-price-row' },
                        React.createElement( 'span', { className: 'cd-price' }, formatPrice( price ) ),
                        React.createElement( 'span', { className: 'cd-chg ' + ( positive ? 'up' : 'down' ) },
                            ( positive ? '\u2191 ' : '\u2193 ' ) + Math.abs( change24h ).toFixed( 2 ) + '%'
                        ),
                        React.createElement( 'span', { className: 'cd-chg-label' }, '(24h)' )
                    )
                )
            )
        ),

        /* Tab Nav */
        React.createElement( "div", { className: "cd-tabs" },
            [
                { id: "chart",   label: "Chart"   },
                { id: "markets", label: "Markets" },
                { id: "about",   label: "About"   }
            ].map( t =>
                React.createElement( "a", {
                    key: t.id,
                    href: "#cd-" + t.id,
                    onClick: ( e ) => { e.preventDefault(); setActiveTab( t.id ); const el = document.getElementById( "cd-" + t.id ); if ( el ) el.scrollIntoView( { behavior: "smooth", block: "start" } ); },
                    className: "cd-tab" + ( activeTab === t.id ? " active" : "" )
                }, t.label )
            )
        ),

        /* Chart + Stats grid */
        React.createElement( 'div', { id: 'cd-chart', className: 'cd-main-grid' },
            React.createElement( 'div', { className: 'cd-chart-card' },
                React.createElement( 'div', { className: 'cd-timeframe-tabs' },
                    TIMEFRAMES.map( tf =>
                        React.createElement( 'button', {
                            key: tf.id,
                            className: 'cd-tf-btn' + ( timeframe === tf.id ? ' active' : '' ),
                            onClick: () => setTimeframe( tf.id ),
                        }, tf.label )
                    )
                ),
                React.createElement( PriceChart, { data: chartData, positive } )
            ),
            React.createElement( 'div', { className: 'cd-stats-card' },
                stats.map( ( s, i ) =>
                    React.createElement( 'div', { key: i, className: 'cd-stat-row' },
                        React.createElement( 'span', { className: 'cd-stat-label' }, s.label ),
                        React.createElement( 'span', { className: 'cd-stat-value' }, s.value )
                    )
                )
            )
        ),

        /* Price changes panel */
        React.createElement( 'div', { className: 'cd-section' },
            React.createElement( 'h2', { className: 'cd-section-title' }, 'Price Changes' ),
            React.createElement( 'div', { className: 'cd-changes-grid' },
                changes.map( ( c, i ) =>
                    React.createElement( 'div', { key: i, className: 'cd-change-card' },
                        React.createElement( 'div', { className: 'cd-change-label' }, c.label ),
                        React.createElement( 'div', { className: 'cd-change-value ' + ( ( c.v || 0 ) >= 0 ? 'up' : 'down' ) },
                            c.v != null ? ( ( c.v >= 0 ? '\u2191 ' : '\u2193 ' ) + Math.abs( c.v ).toFixed( 2 ) + '%' ) : '-'
                        )
                    )
                )
            )
        ),

        /* Markets table */
        React.createElement( "div", { id: "cd-markets", className: "cd-section" },
            React.createElement( "h2", { className: "cd-section-title" }, coin.name + " Markets" ),
            markets.length === 0
                ? React.createElement( "p", { className: "cd-empty" }, "No market data available yet." )
                : React.createElement( "div", { className: "cd-markets-table-wrap" },
                    React.createElement( "table", { className: "cd-markets-table" },
                        React.createElement( "thead", null,
                            React.createElement( "tr", null,
                                [ "#", "Exchange", "Pair", "Price", "Volume", "Trust" ].map( ( h, i ) =>
                                    React.createElement( "th", { key: i }, h )
                                )
                            )
                        ),
                        React.createElement( "tbody", null,
                            markets.map( ( m, i ) => {
                                const trust = m.trust_score;
                                const trustColor = trust === "green" ? "#16a34a" : trust === "yellow" ? "#eab308" : "#6b7280";
                                return React.createElement( "tr", { key: i },
                                    React.createElement( "td", null, i + 1 ),
                                    React.createElement( "td", null,
                                        React.createElement( "a", { href: m.trade_url || "#", target: "_blank", rel: "noopener noreferrer", className: "cd-market-exchange" },
                                            m.market && m.market.logo && React.createElement( "img", { src: m.market.logo, alt: m.market.name, className: "cd-market-logo", onError: e => { e.target.style.display = "none"; } } ),
                                            React.createElement( "span", null, ( m.market && m.market.name ) || "-" )
                                        )
                                    ),
                                    React.createElement( "td", null, (function(){ const fmt = (s) => { if (!s) return ""; if (s.length === 42 && s.toLowerCase().indexOf("0x") === 0) return s.slice(0,6) + "…" + s.slice(-4); return s; }; return fmt(m.base) + "/" + fmt(m.target); })() ),
                                    React.createElement( "td", null, formatPrice( m.last ) ),
                                    React.createElement( "td", null, formatLarge( ( m.converted_volume && m.converted_volume.usd ) || 0 ) ),
                                    React.createElement( "td", null,
                                        React.createElement( "span", { className: "cd-trust-dot", style: { background: trustColor } } )
                                    )
                                );
                            } )
                        )
                    )
                )
        ),

        /* Similar Coins */
        similarCoins.length > 0 && React.createElement( "div", { className: "cd-section" },
            React.createElement( "h2", { className: "cd-section-title" }, "Similar to " + coin.name ),
            React.createElement( "div", { className: "cd-similar-grid" },
                similarCoins.map( ( c, i ) => {
                    const pos = ( c.price_change_percentage_24h || 0 ) >= 0;
                    return React.createElement( "div", {
                        key: i,
                        className: "cd-similar-card",
                        onClick: () => navigate( "/market/coin/" + c.id ),
                    },
                        React.createElement( "img", { src: c.image, alt: c.name, className: "cd-similar-img" } ),
                        React.createElement( "div", { className: "cd-similar-info" },
                            React.createElement( "div", { className: "cd-similar-name" }, c.name ),
                            React.createElement( "div", { className: "cd-similar-symbol" }, ( c.symbol || "" ).toUpperCase() )
                        ),
                        React.createElement( "div", { className: "cd-similar-price" },
                            React.createElement( "div", null, formatPrice( c.current_price ) ),
                            React.createElement( "div", { className: "cd-similar-chg " + ( pos ? "up" : "down" ) },
                                ( pos ? "\u2191 " : "\u2193 " ) + Math.abs( c.price_change_percentage_24h || 0 ).toFixed( 2 ) + "%"
                            )
                        )
                    );
                } )
            )
        ),

        /* About — always shown */
        React.createElement( "div", { id: "cd-about", className: "cd-section" },
            React.createElement( AdSlot, { html: getAdSlot4() } ),
            React.createElement( "h2", { className: "cd-section-title" }, "About " + coin.name ),
            coin.description && coin.description.en
                ? React.createElement( "div", { className: "cd-about", dangerouslySetInnerHTML: { __html: coin.description.en } } )
                : React.createElement( "p", { className: "cd-empty" }, "No description available for " + coin.name + " yet." )
        ),

        /* (Old About kept inactive) */
        false && coin.description?.en && React.createElement( 'div', { className: 'cd-section' },
            React.createElement( 'h2', { className: 'cd-section-title' }, 'About ' + coin.name ),
            React.createElement( 'div', {
                className: 'cd-about',
                dangerouslySetInnerHTML: { __html: coin.description.en.split( '\n' )[ 0 ] || coin.description.en },
            } )
        ),

        /* Links */
        React.createElement( 'div', { className: 'cd-section' },
            React.createElement( 'h2', { className: 'cd-section-title' }, 'Resources' ),
            React.createElement( 'div', { className: 'cd-links-grid' },
                coin.links?.homepage?.[ 0 ] && React.createElement( 'a', { href: coin.links.homepage[ 0 ], target: '_blank', rel: 'noopener noreferrer', className: 'cd-link' }, '\u{1F310} Website' ),
                coin.links?.whitepaper && React.createElement( 'a', { href: coin.links.whitepaper, target: '_blank', rel: 'noopener noreferrer', className: 'cd-link' }, '\u{1F4C4} Whitepaper' ),
                coin.links?.blockchain_site?.[ 0 ] && React.createElement( 'a', { href: coin.links.blockchain_site[ 0 ], target: '_blank', rel: 'noopener noreferrer', className: 'cd-link' }, '\u{1F50D} Explorer' ),
                coin.links?.subreddit_url && React.createElement( 'a', { href: coin.links.subreddit_url, target: '_blank', rel: 'noopener noreferrer', className: 'cd-link' }, '\u{1F4AC} Reddit' ),
                coin.links?.twitter_screen_name && React.createElement( 'a', { href: 'https://twitter.com/' + coin.links.twitter_screen_name, target: '_blank', rel: 'noopener noreferrer', className: 'cd-link' }, '\u{1F426} Twitter' ),
                coin.links?.repos_url?.github?.[ 0 ] && React.createElement( 'a', { href: coin.links.repos_url.github[ 0 ], target: '_blank', rel: 'noopener noreferrer', className: 'cd-link' }, '\u{1F4BB} GitHub' )
            )
        ),

        /* News */
        news.length > 0 && React.createElement( 'div', { className: 'cd-section' },
            React.createElement( 'h2', { className: 'cd-section-title' }, coin.name + ' Latest News' ),
            React.createElement( 'div', { className: 'cd-news-grid' },
                news.slice( 0, 4 ).map( ( n, i ) =>
                    React.createElement( 'a', {
                        key: i, href: n.link, target: '_blank', rel: 'noopener noreferrer',
                        className: 'cd-news-card',
                    },
                        n.thumbnail && React.createElement( 'img', { src: n.thumbnail, alt: n.title, className: 'cd-news-img', onError: e => { e.target.style.display = 'none'; } } ),
                        React.createElement( 'div', { className: 'cd-news-body' },
                            React.createElement( 'h3', { className: 'cd-news-title' }, n.title ),
                            React.createElement( 'p', { className: 'cd-news-date' }, new Date( n.pubDate ).toLocaleDateString() )
                        )
                    )
                )
            )
        )
    );
}

export default CoinDetailPage;
